import type { BasketItem } from '@scayle/storefront-nuxt'
import { getTotalPriceWithoutReductions } from '@scayle/storefront-basket/utils'
import { computed } from 'vue'
import { toCurrency } from '#layers/base/utils/price'
import { useRouter } from '#app/composables/router'
import useAgsBasket from '~/composables/useAgsBasket'
import { routeList } from '~/utils/route'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import useLanguageSwitch from '#layers/base/composables/useLanguageSwitch'

export const ShippingPriceMap: Record<string, number> = {
  DE: 595,
  AT: 595,
  CH: 795,
  NL: 595,
  BE: 495,
  FR: 750,
  GB: 495,
}

export default function useBasketSummary() {
  const router = useRouter()
  const basket = useAgsBasket()
  const { data: shopConfiguration } = useSmartShopConfiguration()
  const { getCountryCode, currentIsoCode } = useLanguageSwitch()

  const { toLocalePath } = useRouteHelpers()

  const hasDiscounts = computed(() => appliedDiscounts.value > 0)

  const areOnlyGiftcardItems = computed(() =>
    basket.items.value.every((basketItem: BasketItem) =>
      isGiftcard(basketItem?.product),
    ),
  )

  const premiumClubItem = computed(() =>
    basket.items.value.find((basketItem: BasketItem) =>
      isPremiumClubProduct(basketItem?.product),
    ),
  )

  const shippingPrice = computed<number>(() => {
    const isFreeShipping = premiumClubItem.value || areOnlyGiftcardItems.value

    if (isFreeShipping || typeof currentIsoCode !== 'string') {
      return 0
    }
    const deliveryCost =
      shopConfiguration.value &&
      'DeliveryCost' in shopConfiguration.value.customData
        ? (shopConfiguration.value?.customData?.DeliveryCost as number)
        : undefined

    if (deliveryCost || deliveryCost === 0) {
      return deliveryCost
    }

    const countryCode = getCountryCode(currentIsoCode)
    const countryKey = Object.keys(ShippingPriceMap).find(
      (key) => key === countryCode,
    )
    return countryKey ? (ShippingPriceMap[countryKey] as number) : 0
  })

  const totalPrice = computed((): string | undefined => {
    return basket.data.value?.cost?.withTax
      ? toCurrency(basket.data.value.cost.withTax + Number(shippingPrice.value))
      : undefined
  })

  const appliedDiscounts = computed(() => {
    const totalReductions =
      basket.cost.value?.appliedReductions.reduce((total, reduction) => {
        return total + reduction.amount.absoluteWithTax
      }, 0) ?? 0

    return Math.abs(totalReductions)
  })
  const onClickToCheckoutOrder = () => {
    router.push(toLocalePath({ name: routeList.checkout.name }))
  }

  const isBuyBoxSticky = computed<boolean>(
    () => (basket.data.value?.items?.length || 0) > 3,
  )

  const formattedShippingPrice = computed<string | undefined>(() =>
    typeof shippingPrice.value !== 'undefined'
      ? toCurrency(+shippingPrice.value)
      : undefined,
  )

  const originalTotal = computed(() => {
    return basket.cost.value
      ? getTotalPriceWithoutReductions(basket.cost.value)
      : undefined
  })
  const formattedDiscount = computed(() =>
    hasDiscounts.value ? toCurrency(-appliedDiscounts.value) : undefined,
  )

  const formattedOriginalTotal = computed(() =>
    originalTotal.value ? toCurrency(originalTotal.value) : undefined,
  )

  const subTotal = computed(() =>
    basket.data.value?.cost?.withTax
      ? toCurrency(basket.data.value.cost.withTax)
      : undefined,
  )

  return {
    onClickToCheckoutOrder,
    isBuyBoxSticky,
    formattedOriginalTotal,
    formattedDiscount,
    formattedShippingPrice,
    totalPrice,
    subTotal,
    hasDiscounts,
  }
}
